import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  Stack,
  StickerLabel,
  Radio,
  Inline,
  ButtonProps,
} from '@stitch-fix/mode-react';
import { SelectEvent, useSelectEvent } from '@stitch-fix/event-reporter';
import AddToBagButton from '../../../../AddToBagButton';
import MoveToBag from '../savedItems/MoveToBag';
import {
  ShopProductPurchaseOptionsVariants,
  ShopProductPurchaseOption,
  ShopProductDetailsCta,
  ShopProductDetailsCtaVariant,
} from '../../../../../../types/definitions/vendor/graphql-schema.d';
import { stickerLabelThemeMap } from '../../../../../utils/maps';

import styles from './FixPurchaseOptions.module.scss';
import BuyNow from '../shared/BuyNow/BuyNow';

export type FixPurchaseOptionsProps = {
  fixPurchaseOptions: ShopProductPurchaseOptionsVariants;
  isItemSaved: boolean;
  isOutOfStock: boolean;
  selectedOptionVariant?: string;
};

type OptionProps = {
  fixPurchaseOption: ShopProductPurchaseOption;
  isSelected: boolean;
  onChange: (variant: string) => void;
};

const shopProductDetailsCtaVariantMap: Record<
  ShopProductDetailsCtaVariant,
  ButtonProps['variant']
> = {
  FILLED_STAPLE: 'filled-staple',
  FILLED_STATEMENT: 'filled-statement',
};

const PurchaseButton = ({ cta }: { cta: ShopProductDetailsCta }) => {
  const { reportSelectEvent } = useSelectEvent();

  if (cta.actionType === 'BUY_NOW') {
    return <BuyNow label={cta.text} />;
  }

  const handleClick = () => {
    if (cta.productTrackingName && cta.productTrackingActionName) {
      reportSelectEvent({
        schema: 'select',
        name: cta.productTrackingName as SelectEvent['name'],
        action_name:
          cta.productTrackingActionName as SelectEvent['action_name'],
      });
    }
  };

  return (
    <Button
      disabled={cta.disabled}
      variant={shopProductDetailsCtaVariantMap[cta.variant]}
      width="fill"
      {...(cta.url ? { as: 'a', href: cta.url } : {})}
      onClick={handleClick}
    >
      {cta.text}
    </Button>
  );
};

const SecondaryCta = ({
  selectedOption,
  isItemSaved,
}: {
  selectedOption: ShopProductPurchaseOption;
  isItemSaved: boolean;
}) => {
  const secondaryCtas = selectedOption.secondaryCtas ?? [];

  if (secondaryCtas.length === 0) return null;

  const actionType = isItemSaved ? 'MOVE_TO_BAG' : 'ADD_TO_BAG';
  const matchingCta =
    secondaryCtas.find(cta => cta.actionType === actionType) ||
    secondaryCtas[0];

  return (
    <Box className={styles.fadeIn} width="100%">
      {secondaryCtas.length === 1 ? (
        <PurchaseButton cta={secondaryCtas[0]} />
      ) : (
        matchingCta &&
        (isItemSaved ? (
          <MoveToBag label={matchingCta.text} />
        ) : (
          <AddToBagButton label={matchingCta.text} />
        ))
      )}
    </Box>
  );
};

const Option = ({ fixPurchaseOption, isSelected, onChange }: OptionProps) => {
  const { disabled, label, description, stickerLabel, variant } =
    fixPurchaseOption;

  return (
    <Radio
      name={label}
      isSelected={isSelected}
      onChange={() => onChange(variant)}
      variant="boxed"
      disabled={disabled}
      minWidth="100%"
    >
      <Box>
        <Inline spacing={0} vAlign="center">
          <Text
            scale="3"
            height="standard"
            family="regular"
            color={disabled ? 'gray-45' : 'gray-16'}
            className={styles.optionLabel}
          >
            {label}
          </Text>
          {stickerLabel && (
            <StickerLabel
              ml={0.5}
              variant={stickerLabelThemeMap[stickerLabel.theme]}
            >
              {stickerLabel.label}
            </StickerLabel>
          )}
        </Inline>
        <Text setting="body-small" color={disabled ? 'gray-45' : 'gray-16'}>
          {description}
        </Text>
      </Box>
    </Radio>
  );
};

const FixPurchaseOptions = ({
  fixPurchaseOptions,
  isItemSaved,
  isOutOfStock,
  selectedOptionVariant,
}: FixPurchaseOptionsProps) => {
  const purchaseOptions = isOutOfStock
    ? fixPurchaseOptions.outOfStock
    : fixPurchaseOptions.inStock;

  const [selectedOption, setSelectedOption] = useState<
    ShopProductPurchaseOption | undefined
  >(() => {
    return (
      purchaseOptions?.options.find(
        option => option.variant === selectedOptionVariant && !option.disabled,
      ) || purchaseOptions?.options.find(option => option.selected)
    );
  });

  useEffect(() => {
    if (selectedOption) {
      const newSelectedOption =
        purchaseOptions?.options.find(
          option =>
            option.variant === selectedOption.variant && !option.disabled,
        ) || purchaseOptions?.options.find(option => option.selected);

      if (newSelectedOption && newSelectedOption !== selectedOption) {
        setSelectedOption(newSelectedOption);
      }
    }
  }, [fixPurchaseOptions, purchaseOptions?.options, selectedOption]);

  const handleOnChange = (variant: string) => {
    const newOption = purchaseOptions?.options.find(
      option => option.variant === variant,
    );

    if (newOption && newOption.variant !== selectedOption?.variant) {
      setSelectedOption(newOption);
    }
  };

  const primaryCta = selectedOption?.primaryCta;

  return (
    <Box>
      <Stack spacing={0.5}>
        {purchaseOptions?.options.map((option: ShopProductPurchaseOption) => (
          <Option
            key={option.variant}
            fixPurchaseOption={option}
            onChange={id => handleOnChange(id)}
            isSelected={option.variant === selectedOption?.variant}
          />
        ))}
      </Stack>

      {selectedOption && (
        <Box aria-live="polite">
          <Box
            key={selectedOption.variant}
            style={{ display: 'flex', gap: '16px', marginTop: '24px' }}
          >
            {primaryCta && (
              <Box className={styles.fadeIn} width="100%">
                <PurchaseButton cta={primaryCta} />
              </Box>
            )}
            <SecondaryCta
              selectedOption={selectedOption}
              isItemSaved={isItemSaved}
            />
          </Box>
        </Box>
      )}

      {purchaseOptions && purchaseOptions.cta && (
        <PurchaseButton cta={purchaseOptions.cta} />
      )}
    </Box>
  );
};

export default FixPurchaseOptions;
